import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	ReactNode,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";

interface Company {
	id: string;
	name: string;
	isDemo: boolean;
	role?: string;
	plan?: string;
}

interface CompanyContextType {
	companies: Company[];
	activeCompany: Company | null;
	isLoading: boolean;
	error: string | null;
	switchCompany: (companyId: string) => Promise<void>;
	refreshCompanies: () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

const STORAGE_KEY = "selectedCompanyId";

export const CompanyProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const { user, isAuthenticated } = useAuth0();
	const [companies, setCompanies] = useState<Company[]>([]);
	const [activeCompany, setActiveCompany] = useState<Company | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Load companies from user metadata
	useEffect(() => {
		if (!isAuthenticated || !user) {
			setIsLoading(false);
			return;
		}

		loadCompanies();
		// TODO(lint): exhaustive-deps disabled — needs careful review of dep array
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAuthenticated, user]);

	const loadCompanies = async () => {
		try {
			setIsLoading(true);
			setError(null);

			// Get company info from Auth0 user metadata
			const userMetadata = user?.["https://arteh.com.au/user_metadata"];

			if (!userMetadata) {
				setCompanies([]);
				setIsLoading(false);
				return;
			}

			// For now, we'll use the single company from metadata
			// In the future, this could be an array of companies
			// Metadata shape varies by how the user was provisioned (signup,
			// invitation, claim): fall back through every known field.
			const appMetadata = user?.["https://arteh.com.au/app_metadata"];
			const companyId =
				userMetadata.companyId ||
				userMetadata.cpID ||
				userMetadata.activeCompanyId;
			const metaCompany = (appMetadata?.companies || []).find(
				(c: any) => String(c.id) === String(companyId),
			);
			const companyName =
				userMetadata.companyName ||
				userMetadata.activeCompanyName ||
				metaCompany?.name ||
				"Company";

			// Create company list
			const companyList: Company[] = [
				{
					id: companyId,
					name: companyName,
					isDemo: false,
					role: "Admin",
					plan: "Premium",
				},
			];

			// Add demo company if it exists
			if (userMetadata.demoCompanyId) {
				companyList.push({
					id: userMetadata.demoCompanyId,
					name: "Demo Company",
					isDemo: true,
					role: "Viewer",
					plan: "Demo",
				});
			}

			setCompanies(companyList);

			// Select initial company
			const savedCompanyId = localStorage.getItem(STORAGE_KEY);
			const initialCompany = savedCompanyId
				? companyList.find((c) => c.id === savedCompanyId) ||
					companyList[0]
				: companyList[0];

			if (initialCompany) {
				setActiveCompany(initialCompany);
				localStorage.setItem(STORAGE_KEY, initialCompany.id);
				console.log("[ARTEH] Session info", {
					userId: user?.sub,
					email: user?.email,
					companyId: initialCompany.id,
					companyName: initialCompany.name,
					activeCompanyId: userMetadata.activeCompanyId,
					plan: user?.["https://arteh.com.au/app_metadata"]
						?.current_plan,
					isDemoMode: userMetadata.isDemoMode,
				});
			}
		} catch (err) {
			console.error("Failed to load companies:", err);
			setError("Failed to load companies");
		} finally {
			setIsLoading(false);
		}
	};

	const switchCompany = async (companyId: string) => {
		const company = companies.find((c) => c.id === companyId);

		if (!company) {
			throw new Error("Company not found");
		}

		// Update active company
		setActiveCompany(company);
		localStorage.setItem(STORAGE_KEY, companyId);
		console.log("[ARTEH] Switched company", {
			userId: user?.sub,
			companyId: company.id,
			companyName: company.name,
		});

		// Dispatch custom event for other components to listen to
		window.dispatchEvent(
			new CustomEvent("companyChanged", {
				detail: {
					previousCompany: activeCompany,
					newCompany: company,
				},
			}),
		);
	};

	const refreshCompanies = async () => {
		await loadCompanies();
	};

	return (
		<CompanyContext.Provider
			value={{
				companies,
				activeCompany,
				isLoading,
				error,
				switchCompany,
				refreshCompanies,
			}}
		>
			{children}
		</CompanyContext.Provider>
	);
};

export const useCompany = () => {
	const context = useContext(CompanyContext);
	if (context === undefined) {
		throw new Error("useCompany must be used within a CompanyProvider");
	}
	return context;
};

export default CompanyContext;
